import organization from './organization.json'
import website from './website.json'
import article from './article.json'

const schema = {
  website,
  organization,
  article,
}

const generateArticleSchema = (article) => {
  if (!article) return

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    datePublished: article.createdAt,
    dateModified: article.updatedAt,
    name: article.title,
    headline: article.title,
    description: article.description,
    articleBody: parseHtmlText(article.articleBody),
    image: article.image.src,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://bungalow.com/articles/${article.articleSlug}`,
    },
    author: {
      '@type': 'Person',
      name: 'Bungalow Team',
    },
    publisher: organization,
  }
}

const generatePropertyList = (market, listings) => {
  const itemListElements = listings.map((property, index) => {
    const offering =
      property.propertyMarketingType === 'co_living' ? 'rooms-for-rent' : 'homes-for-rent'
    return {
      '@context': 'https://schema.org',
      '@type': 'ListItem',
      item: {
        '@id': `https://bungalow.com/${offering}/${market.slug}/${property.slug}`,
        position: index + 1,
        name: property.headline,
        url: `https://bungalow.com/${offering}/${market.slug}/${property.slug}`,
      },
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemListElements,
  }
}

const parseHtmlText = (html = '') => {
  const removeHTMLRegex = /(<([^>]+)>)/gi

  return html.replace(removeHTMLRegex, '')
}

const generatePropertySchema = ({ market, ...property }) => {
  const propertyImage = (property.images.length && property.images[0].smUrl) || ''
  const offering =
    property.propertyMarketingType === 'co_living' ? 'rooms-for-rent' : 'homes-for-rent'
  const propertyUrl = `https://bungalow.com/${offering}/${market.slug}/${property.slug}`
  const propertyDescription = parseHtmlText(property.descriptionHtml)

  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: property.headline,
    image: propertyImage,
    description: propertyDescription,
    brand: {
      '@type': 'Organization',
      name: 'Bungalow',
      url: 'https://www.bungalow.com',
      email: 'help@bungalow.com',
    },
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: property.priceMin || 0,
      highPrice: property.priceMax || 0,
      priceCurrency: 'USD',
      offerCount: property.availableRoomCount,
    },
  }

  const singleFamilyResidence = {
    '@context': 'https://schema.org',
    '@type': 'SingleFamilyResidence',
    name: property.headline,
    numberofRooms: property.totalRoomCount,
    url: propertyUrl,
  }

  const realEstateListing = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    'datePosted:': property.earliestAvailableDate,
  }

  const imageObjects = property.images.map((image) => ({
    '@type': 'ImageObject',
    contentUrl: image.mdUrl,
    thumbnailUrl: image.smUrl,
    url: propertyUrl,
  }))

  const imageGallery = {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    abstract: property.headline,
    url: propertyUrl,
    associatedMedia: imageObjects,
  }

  return [product, singleFamilyResidence, realEstateListing, imageGallery]
}

const generatePropertyListingSchema = ({ market, ...property }) => {
  const propertyImage = (property.images.length && property.images[0].smUrl) || ''
  const offering =
    property.propertyMarketingType === 'co_living' ? 'rooms-for-rent' : 'homes-for-rent'
  const propertyUrl = `https://bungalow.com/${offering}/${market.slug}/${property.slug}`
  const amenityList = property.amenities.map((amenity) => amenity.displayName)

  return {
    '@context': 'https://schema.org',
    '@type': 'Apartment',
    name: property.headline,
    photo: propertyImage,
    url: propertyUrl,
    tourBookingPage: `${propertyUrl}?book=true`,
    numberOfRooms: property.totalRoomCount,
    amenityFeature: amenityList,
    latitude: property.address.latitude,
    longitude: property.address.longitude,
    address: {
      '@type': 'PostalAddress',
      addressLocality: market.displayName,
      addressRegion: market.region,
    },
    containedInPlace: {
      '@type': 'City',
      name: property.address.city,
      url: `https://bungalow.com/${offering}/${market.slug}`,
    },
  }
}

const generateMarketsSchema = (markets = []) => {
  let position = 1

  const itemListElements = markets.flatMap((market) => {
    const roomForRent = {
      '@type': 'ListItem',
      position: position++,
      item: {
        '@id': `https://bungalow.com/rooms-for-rent/${market.slug}`,
        name: `Rooms for Rent in ${market.displayName}`,
        url: `https://bungalow.com/rooms-for-rent/${market.slug}`,
        image: market.img,
        description: website.abstract,
      },
    }

    const homeForRent = {
      '@type': 'ListItem',
      position: position++,
      item: {
        '@id': `https://bungalow.com/homes-for-rent/${market.slug}`,
        name: `Homes for Rent in ${market.displayName}`,
        url: `https://bungalow.com/homes-for-rent/${market.slug}`,
        image: market.img,
        description: website.abstract,
      },
    }
    return [roomForRent, homeForRent]
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemListElements,
  }
}

const generateListingsSchema = (listings = []) => {
  if (!listings.length) return listings

  const market = (listings[0].market && listings[0].market) || {}
  const marketPrices = listings.reduce((prices, listing) => {
    return prices.concat(listing.priceMin, listing.priceMax)
  }, [])
  const lowestPrice = Math.min(...marketPrices)
  const highestPrice = Math.max(...marketPrices)

  const structuredPropertyData = listings.map(generatePropertyListingSchema)
  const structuredPropertyList = generatePropertyList(market, listings)

  const structuredListingData = {
    '@context': 'https://schema.org',
    '@type': ['Apartment', 'Product'],
    name: `${market.displayName} listings – Bungalow`,
    description: website.abstract,
    image: market.img,
    brand: {
      '@type': 'Organization',
      name: 'Bungalow',
      url: 'https://www.bungalow.com',
      email: 'help@bungalow.com',
    },
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: lowestPrice || 0,
      highPrice: highestPrice || 0,
      priceCurrency: 'USD',
      offerCount: listings.length,
    },
  }

  return [structuredListingData, structuredPropertyList, ...structuredPropertyData]
}

const generateBreadcrumbSchema = (route = {}) => {
  if (!route.path) return

  const baseUrl = 'https://bungalow.com'
  const urlPaths = route.path.split('/')

  const breadcrumbUrls = urlPaths.reduce((crumbs, urlPath, index) => {
    if (!crumbs.length) {
      // Return base breadcrumb path
      return crumbs.concat([{ name: 'Bungalow', url: `${baseUrl}${urlPath}` }])
    }

    // Avoid parsing any empty path segments (e.g. trailing slashes, etc.)
    if (crumbs.length && !urlPath) return crumbs

    // Return incrememntal breadcrumb path
    return crumbs.concat([
      {
        // Prepends the previous crumb
        url: `${crumbs[index - 1].url}/${urlPath}`,
        // Capitalizes the crumb name
        name: `${urlPath[0].toUpperCase() + urlPath.slice(1)}`,
      },
    ])
  }, [])

  const itemListElements = breadcrumbUrls.map((crumb, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': crumb.url,
        name: crumb.name,
      },
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemListElements,
  }
}

export {
  schema,
  generateArticleSchema,
  generateMarketsSchema,
  generateListingsSchema,
  generatePropertySchema,
  generateBreadcrumbSchema,
}
